import type { ReactNode } from 'react'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { ColumnDef, SortDir } from './useColumnConfig'

export interface SortableHeaderProps {
  column: ColumnDef
  sortKey: string | null
  sortDir: SortDir
  onSort: (key: string) => void
  className?: string
  children?: ReactNode
}

/**
 * `<th>` wrapper that handles click-to-sort with tri-state cycle:
 *   asc → desc → off → asc → ...
 * Renders an arrow indicator when the column is the active sort key.
 * Non-sortable columns render plain text without affordance.
 */
export function SortableHeader({
  column,
  sortKey,
  sortDir,
  onSort,
  className,
  children,
}: SortableHeaderProps) {
  const sortable = column.sortable !== false
  const active = sortKey === column.key
  const align = column.align ?? 'left'
  const alignClass =
    align === 'right' ? 'text-right justify-end' :
    align === 'center' ? 'text-center justify-center' :
    'text-left justify-start'

  const content = children ?? column.label

  if (!sortable) {
    return (
      <th
        className={cn(
          'text-role-table-header px-2 py-2 select-none',
          alignClass,
          className,
        )}
        style={column.width ? { width: column.width } : undefined}
      >
        {content}
      </th>
    )
  }

  return (
    <th
      className={cn(
        'text-role-table-header px-2 py-2 select-none cursor-pointer hover:bg-surface-sunk',
        alignClass,
        className,
      )}
      style={column.width ? { width: column.width } : undefined}
      onClick={() => onSort(column.key)}
      aria-sort={
        active
          ? sortDir === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <span className={cn('inline-flex items-center gap-1', alignClass)}>
        {content}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp size={11} className="text-primary" />
          ) : (
            <ChevronDown size={11} className="text-primary" />
          )
        ) : (
          <ChevronsUpDown size={11} className="text-tertiary opacity-40" />
        )}
      </span>
    </th>
  )
}
